import { useEffect, useCallback, useState } from "react";
import { Link, useFetcher, useRouteLoaderData } from "react-router";
import { type loader as rootLoader } from "#app/root.tsx";
import { Button } from "./ui/button.tsx";
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuPortal,
	DropdownMenuContent,
	DropdownMenuItem,
} from "./ui/dropdown-menu.tsx";
import { Icon } from "./ui/icon.tsx";

export function NotificationBell() {
	const rootData = useRouteLoaderData<typeof rootLoader>("root");
	const initialCount = rootData?.unreadNotificationCount ?? 0;

	const listFetcher = useFetcher<{
		notifications: Array<{
			id: string;
			type: string;
			title: string;
			message: string;
			link: string | null;
			read: boolean;
			createdAt: string;
		}>;
		unreadCount: number;
	}>();
	const markReadFetcher = useFetcher();

	const [isOpen, setIsOpen] = useState(false);
	const [polledCount, setPolledCount] = useState<number>();

	// Use the most recent count available
	const unreadCount =
		polledCount ??
		listFetcher.data?.unreadCount ??
		initialCount;

	const notifications = listFetcher.data?.notifications ?? [];

	// Plain fetch, not a fetcher: fetcher errors bubble to the root ErrorBoundary,
	// so a poll that fails after sleep/offline would replace the whole page.
	const refreshCount = useCallback(() => {
		fetch("/resources/notifications?countOnly=true")
			.then((r) =>
				r.ok ? (r.json() as Promise<{ unreadCount: number }>) : null,
			)
			.then((d) => {
				if (d) setPolledCount(d.unreadCount);
			})
			.catch(() => {});
	}, []);

	// Poll for unread count every 30 seconds
	useEffect(() => {
		const interval = setInterval(() => {
			if (!isOpen && !document.hidden) refreshCount();
		}, 30_000);
		return () => clearInterval(interval);
	}, [isOpen, refreshCount]);

	const handleOpen = useCallback(
		(open: boolean) => {
			setIsOpen(open);
			if (open) {
				void listFetcher.load("/resources/notifications");
			}
		},
		[], // eslint-disable-line react-hooks/exhaustive-deps
	);

	function markAllRead() {
		void markReadFetcher.submit(
			{ notificationId: "all" },
			{ method: "POST", action: "/resources/notifications" },
		);
		// Optimistically clear the count
		refreshCount();
	}

	function markOneRead(id: string) {
		void markReadFetcher.submit(
			{ notificationId: id },
			{ method: "POST", action: "/resources/notifications" },
		);
	}

	return (
		<DropdownMenu onOpenChange={handleOpen}>
			<DropdownMenuTrigger asChild>
				<Button variant="ghost" size="icon" className="relative">
					<Icon name="bell" size="md" />
					{unreadCount > 0 && (
						<span className="absolute -right-0.5 -top-0.5 flex size-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
							{unreadCount > 9 ? "9+" : unreadCount}
						</span>
					)}
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent
					sideOffset={8}
					align="end"
					className="w-80 max-h-96 overflow-y-auto"
				>
					<div className="flex items-center justify-between border-b border-border px-3 py-2">
						<span className="text-sm font-semibold">Notifications</span>
						{unreadCount > 0 && (
							<button
								onClick={markAllRead}
								className="text-xs text-muted-foreground hover:text-foreground"
							>
								Mark all read
							</button>
						)}
					</div>

					{notifications.length === 0 && (
						<div className="px-3 py-6 text-center text-sm text-muted-foreground">
							No notifications yet
						</div>
					)}

					{notifications.map((notification) => (
						<DropdownMenuItem
							key={notification.id}
							asChild
							className="flex flex-col items-start gap-0.5 px-3 py-2"
						>
							{notification.link ? (
								<Link
									to={notification.link}
									onClick={() => {
										if (!notification.read) markOneRead(notification.id);
									}}
								>
									<NotificationItem notification={notification} />
								</Link>
							) : (
								<div
									onClick={() => {
										if (!notification.read) markOneRead(notification.id);
									}}
								>
									<NotificationItem notification={notification} />
								</div>
							)}
						</DropdownMenuItem>
					))}
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	);
}

function NotificationItem({
	notification,
}: {
	notification: {
		title: string;
		message: string;
		read: boolean;
		createdAt: string;
	};
}) {
	return (
		<>
			<div className="flex w-full items-center gap-2">
				{!notification.read && (
					<span className="size-2 shrink-0 rounded-full bg-blue-500" />
				)}
				<span className="text-sm font-medium">{notification.title}</span>
			</div>
			<p className="text-xs text-muted-foreground line-clamp-2">
				{notification.message}
			</p>
			<time className="text-[10px] text-muted-foreground">
				{formatTimeAgo(notification.createdAt)}
			</time>
		</>
	);
}

function formatTimeAgo(dateString: string) {
	const date = new Date(dateString);
	const now = new Date();
	const diffMs = now.getTime() - date.getTime();
	const diffMins = Math.floor(diffMs / 60_000);
	const diffHours = Math.floor(diffMs / 3_600_000);
	const diffDays = Math.floor(diffMs / 86_400_000);

	if (diffMins < 1) return "just now";
	if (diffMins < 60) return `${diffMins}m ago`;
	if (diffHours < 24) return `${diffHours}h ago`;
	if (diffDays < 7) return `${diffDays}d ago`;
	return date.toLocaleDateString();
}
